import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';
import { selectOpponent, selectPlayer } from '../ActiveGame/selectors';
import { Button, ButtonRow, Muted } from '../Controls';
import { EnergySymbol, energyName } from '../Energy';
import SelectPile from '../SelectPile';
import { picksCancel, pickCards, pickTarget, pickType } from './actions';
import { DISCARD, HAND, picksPokemon, PLAYER, TYPE } from './constants';
import {
  selectPicksChoosing, selectPicksMade, selectPicksSource, selectPicksStep, selectPicksSteps, selectStepTargets,
} from './selectors';
import { cardsToPick, energiesFor } from './steps';

/** The Pokémon at 'active' or a bench position, from selectPlayer or selectOpponent, with its name. */
const pokemonAt = (owner, at) => {
  if (at === 'active') {
    const active = owner.get('activePokemon');
    return { pokemon: active, name: active.getIn(['card', 'name']) };
  }
  const benched = owner.getIn(['bench', 'cards']).find(card => card.get('order') === at);
  return { pokemon: benched, name: benched.get('name') };
};

/** What finishing a step says: the last one plays the Trainer or uses the Power. */
const useDoneLabel = () => {
  const source = useSelector(selectPicksSource);
  const last = useSelector(selectPicksMade).size === useSelector(selectPicksSteps).size - 1;
  if (!last) {
    return 'Next';
  }
  return source.get('kind') === 'power' ? `Use ${source.get('name')}` : `Play ${source.get('name')}`;
};

const Back = () => {
  const dispatch = useDispatch();
  return <Button onClick={() => dispatch(picksCancel())} $block>Back</Button>;
};

/** Once a step's Pokémon is tapped: which of its energy cards the step takes (Super Potion, Energy Trans). */
const EnergyStep = () => {
  const dispatch = useDispatch();
  const step = useSelector(selectPicksStep);
  const choosing = useSelector(selectPicksChoosing);
  const source = useSelector(selectPicksSource);
  const doneLabel = useDoneLabel();
  const from = choosing.get('from');
  const owner = useSelector(from === PLAYER ? selectPlayer : selectOpponent);
  const { pokemon, name } = pokemonAt(owner, choosing.get('at'));
  const energies = energiesFor(step, pokemon.get('energyAttachments'));
  const count = cardsToPick(step, energies.size);
  const message = from === PLAYER
    ? `Choose ${count} of ${name}'s energy cards for ${source.get('name')}.`
    : `Choose ${count} of ${name}'s energy cards for ${source.get('name')} to discard.`;

  return (
    <>
      <SelectPile
        cardsOrCardables={energies}
        message={message}
        selectCount={count}
        selectedIds={List()}
        doneLabel={doneLabel}
        onSelectDone={ids => dispatch(pickTarget(from, choosing.get('at'), ids))}
      />
      <Back />
    </>
  );
};

/**
 * A step that takes cards from your hand or discard pile, or the opponent's discard pile (Pokémon Flute): only the
 * ones the server says it can take are shown.
 */
const PileStep = () => {
  const dispatch = useDispatch();
  const step = useSelector(selectPicksStep);
  const source = useSelector(selectPicksSource);
  const allowed = useSelector(selectStepTargets);
  const player = useSelector(selectPlayer);
  const opponent = useSelector(selectOpponent);
  const doneLabel = useDoneLabel();
  const from = step.get('from');
  const owner = from === HAND || from === DISCARD ? player : opponent;
  const pile = owner.getIn([from === HAND ? 'hand' : 'discardPile', 'cards'], List())
    .filter(pileCard => allowed.includes(pileCard.get('order')));
  const count = cardsToPick(step, pile.size);
  const cards = count === 1 ? '1 card' : `${count} cards`;
  const where = { [HAND]: 'your hand', [DISCARD]: 'your discard pile' }[from] || "your opponent's discard pile";

  return (
    <>
      <SelectPile
        cardsOrCardables={pile}
        message={`Choose ${cards} from ${where} for ${source.get('name')}.`}
        selectCount={count}
        selectedIds={List()}
        doneLabel={doneLabel}
        onSelectDone={ids => dispatch(pickCards(
          from,
          pile.filter(pileCard => ids.includes(pileCard.get('pivot_id'))).map(pileCard => pileCard.get('order')),
        ))}
      />
      <Back />
    </>
  );
};

/** Buzzap: which type of energy Electrode becomes. */
const TypeStep = () => {
  const dispatch = useDispatch();
  const source = useSelector(selectPicksSource);
  const types = useSelector(selectStepTargets);

  return (
    <>
      <p>Choose the type of energy for {source.get('name')}.</p>
      <ButtonRow role="group" aria-label="Energy types" style={{ flexWrap: 'wrap' }}>
        {types.map(type => (
          <Button key={type} onClick={() => dispatch(pickType(type))}>
            <EnergySymbol type={type} size="1.3em" /> {energyName(type)}
          </Button>
        ))}
      </ButtonRow>
      <Muted>It provides 2 energy of that type.</Muted>
      <Back />
    </>
  );
};

/**
 * The current step, when it is picked in the menu rather than on the board: energy cards on a tapped Pokémon, cards
 * from the hand or discard pile, or an energy type. Each step gets its own picker (the key), so one step's choices
 * never carry into the next. Null when the step is a tap on the board, or nothing is being picked.
 */
const PickSteps = () => {
  const step = useSelector(selectPicksStep);
  const choosing = useSelector(selectPicksChoosing);
  const key = `step-${useSelector(selectPicksMade).size}`;

  if (choosing !== null) {
    return <EnergyStep key={key} />;
  }
  if (!step || picksPokemon(step)) {
    return null;
  }
  return step.get('from') === TYPE ? <TypeStep key={key} /> : <PileStep key={key} />;
};

export default PickSteps;
